// Acoustic upload dialog. Folder picks keep their relative path.
// The batch card is portalled: an open Dialog aria-hides the app root.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  InputAdornment,
  LinearProgress,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Link as LinkIcon,
  DescriptionOutlined as FileIcon,
  EqualizerOutlined as ChartIcon,
  FileDownloadOutlined as ExportIcon,
  FolderOpen as FolderIcon,
  CheckCircle as CheckIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import useFileUpload, {
  BatchProgress,
  FileUploadStatus,
  UploadFileItem,
  UploadResult,
} from 'src/hooks/useFileUpload';
import useDeploymentUploadStatus from 'src/hooks/useDeploymentUploadStatus';

const ACCEPTED_EXTENSIONS = ['wav'];
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(',');
const FORMAT_BULLET = `Only ${ACCEPTED_EXTENSIONS.map((ext) => `.${ext}`).join(
  ', ',
)} audio files`;

type UploadTab = 'files' | 'folder' | 'drive';

const idleProgress = (): BatchProgress => ({
  phase: 'idle',
  uploadedCount: 0,
  totalCount: 0,
  failedCount: 0,
  overallPct: 0,
  files: [],
});

const FILE_STATUS_LABEL: Record<FileUploadStatus, string> = {
  queued: 'Waiting…',
  uploading: 'Uploading…',
  done: 'Uploaded',
  error: 'Failed',
};

const PANEL_BY_TAB: Record<
  UploadTab,
  { heading: [string, string]; items: { label: string; icon: typeof FileIcon }[] }
> = {
  files: {
    heading: ['Audio File', 'Requirements'],
    items: [
      { label: FORMAT_BULLET, icon: ChartIcon },
      { label: 'No limit on files per batch', icon: CloudUploadIcon },
      { label: 'Unique file names, or use the folder tab', icon: FileIcon },
    ],
  },
  folder: {
    heading: ['Folder', 'Requirements'],
    items: [
      { label: 'Every file in the folder is uploaded', icon: FolderIcon },
      { label: 'No limit on files', icon: CloudUploadIcon },
    ],
  },
  // Placeholder until the Drive import endpoint exists.
  drive: {
    heading: ['Drive Link', 'Requirements'],
    items: [
      { label: 'Folder or single-file link', icon: LinkIcon },
      { label: 'Shared publicly with anyone-with-view access', icon: ExportIcon },
    ],
  },
};

interface UploadAudioModalProps {
  open: boolean;
  onClose: () => void;
  deploymentId?: string;
  onUploaded?: (results: UploadResult[]) => void;
}

interface BatchCardProps {
  progress: BatchProgress;
  settled: boolean;
  failed: boolean;
  onDismiss: () => void;
}

interface BatchSlot {
  tag: string;
  bytes: number;
  progress: BatchProgress;
}

const BatchProgressCard: React.FC<BatchCardProps> = ({
  progress,
  settled,
  failed,
  onDismiss,
}) => {
  const accent = failed
    ? { main: '#F59E0B', border: 'rgba(245, 158, 11, 0.35)', bg: 'rgba(245, 158, 11, 0.08)' }
    : { main: '#10B981', border: 'rgba(16, 185, 129, 0.35)', bg: 'rgba(16, 185, 129, 0.08)' };

  return (
    <Box
    sx={{
      position: 'fixed',
      bottom: 24,
      right: 24,
      width: 380,
      zIndex: 1400,
      p: 2,
      borderRadius: '12px',
      border: `1px solid ${accent.border}`,
      background: '#0C0C0C',
      boxShadow: '0 16px 40px rgba(0,0,0,0.6)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        mb: 1.5,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography sx={{ fontSize: '12.5px', fontWeight: 800, color: '#fff' }}>
          {settled ? (failed ? 'Upload failed' : 'Upload complete') : 'Uploading files'}
        </Typography>
        <Box
          sx={{
            fontSize: '10px',
            fontWeight: 800,
            color: accent.main,
            border: `1px solid ${accent.border}`,
            background: accent.bg,
            borderRadius: '999px',
            px: 1,
            py: 0.1,
          }}
        >
          {progress.uploadedCount}/{progress.totalCount}
        </Box>
      </Box>
      {settled && (
        <Button
          size="small"
          onClick={onDismiss}
          sx={{ fontSize: '10px', fontWeight: 700, color: '#666' }}
        >
          Dismiss
        </Button>
      )}
    </Box>

    <LinearProgress
      variant="determinate"
      value={progress.overallPct}
      sx={{
        height: 8,
        borderRadius: 4,
        backgroundColor: 'rgba(255,255,255,0.08)',
        '& .MuiLinearProgress-bar': {
          backgroundColor: accent.main,
          borderRadius: 4,
        },
      }}
    />

    <Box
      sx={{
        mt: 1.5,
        maxHeight: 240,
        overflowY: 'auto',
        '&::-webkit-scrollbar': { width: '6px' },
        '&::-webkit-scrollbar-thumb': { background: '#333', borderRadius: '4px' },
      }}
    >
      {progress.files.map((entry) => (
        <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.4 }}>
          {entry.status === 'done' ? (
            <CheckIcon sx={{ fontSize: 13, color: '#10B981', flexShrink: 0 }} />
          ) : entry.status === 'error' ? (
            <ErrorIcon sx={{ fontSize: 13, color: '#F59E0B', flexShrink: 0 }} />
          ) : (
            <FileIcon sx={{ fontSize: 12, color: '#555', flexShrink: 0 }} />
          )}
          <Typography
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: '11px',
              color: '#bbb',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {entry.name}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              fontWeight: 700,
              flexShrink: 0,
              color:
                entry.status === 'error'
                  ? '#F59E0B'
                  : entry.status === 'done'
                    ? '#10B981'
                    : '#666',
            }}
          >
            {entry.status === 'uploading' && entry.pct > 0
              ? `${entry.pct}%`
              : FILE_STATUS_LABEL[entry.status]}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
  );
};

const GoogleDriveLogo = (props: React.SVGProps<SVGSVGElement>) => (
  <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M8.002 15.467L4.002 22.396L0 15.467L7.785 1.98L11.784 8.911L7.999 15.467z" fill="#00A859" />
    <path d="M23.784 15.092H15.785L7.999 1.605H16.001L23.786 15.091z" fill="#0066DA" />
    <path d="M4.435 22.396L8.433 15.467L24 15.467L20.002 22.396L4.435 22.396z" fill="#FFD000" />
  </svg>
);

const WaveGraphic = () => (
  <svg width="100%" height="45" viewBox="0 0 200 45" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ display: 'block', marginTop: 'auto' }}>
    <path d="M0 25 C30 5, 50 40, 80 25 C110 5, 130 40, 160 25 C180 15, 190 25, 200 25" stroke="#10B981" strokeWidth="1.5" strokeOpacity="0.4" fill="none" />
    <path d="M0 30 C20 15, 40 35, 70 25 C100 10, 120 35, 150 25 C170 15, 190 30, 200 25" stroke="#10B981" strokeWidth="1" strokeOpacity="0.2" fill="none" />
  </svg>
);

const TabButton: React.FC<{
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}> = ({ active, icon, label, onClick }) => (
  <Button
    variant="outlined"
    size="small"
    startIcon={icon}
    onClick={onClick}
    sx={{
      fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
      fontSize: '10.5px',
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
      borderRadius: '6px',
      height: '34px',
      px: 2,
      color: active ? '#10B981' : '#666',
      borderColor: active ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.08)',
      background: active ? 'rgba(16, 185, 129, 0.04)' : 'transparent',
      '&:hover': {
        borderColor: active ? '#10B981' : 'rgba(255, 255, 255, 0.15)',
        background: active ? 'rgba(16, 185, 129, 0.08)' : 'rgba(255,255,255,0.02)',
      },
    }}
  >
    {label}
  </Button>
);

const UploadAudioModal: React.FC<UploadAudioModalProps> = ({
  open,
  onClose,
  deploymentId,
  onUploaded,
}) => {
  const [activeTab, setActiveTab] = useState<UploadTab>('files');
  const [driveUrl, setDriveUrl] = useState('');
  const [selected, setSelected] = useState<UploadFileItem[]>([]);
  const [batches, setBatches] = useState<BatchSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const batchSeq = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);
  const { uploadFiles } = useFileUpload();
  const { complete } = useDeploymentUploadStatus(deploymentId, open);

  const progress = useMemo<BatchProgress>(() => {
    const total = (read: (slot: BatchSlot) => number) =>
      batches.reduce((sum, slot) => sum + read(slot), 0);
    const bytesDone = total((slot) => (slot.progress.overallPct / 100) * slot.bytes);
    const totalBytes = total((slot) => slot.bytes);
    return {
      phase:
        batches.length === 0
          ? 'idle'
          : batches.every((slot) => slot.progress.phase === 'done')
            ? 'done'
            : 'uploading',
      uploadedCount: total((slot) => slot.progress.uploadedCount),
      totalCount: total((slot) => slot.progress.totalCount),
      failedCount: total((slot) => slot.progress.failedCount),
      overallPct:
        totalBytes > 0 ? Math.round((bytesDone / totalBytes) * 100) : 0,
      files: batches.flatMap((slot) =>
        slot.progress.files.map((entry) => ({
          ...entry,
          id: `${slot.tag}/${entry.id}`,
        })),
      ),
    };
  }, [batches]);

  const busy = progress.phase === 'uploading';

  const batchSettled = progress.phase === 'done' && progress.totalCount > 0;
  const showingBatch = busy || batchSettled;

  const failed = progress.failedCount > 0;

  const panel = PANEL_BY_TAB[activeTab];

  const reset = useCallback(() => {
    setSelected([]);
    setBatches([]);
    setError(null);
  }, []);

  useEffect(() => {
    if (!open) return;
    setSelected([]);
    setError(null);
  }, [open, deploymentId]);

  useEffect(() => {
    setBatches([]);
  }, [deploymentId]);

  const collect = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    const items: UploadFileItem[] = [];
    for (let i = 0; i < list.length; i++) {
      const file = list.item(i);
      if (!file) continue;
      const relative = (file as File & { webkitRelativePath?: string })
        .webkitRelativePath;
      items.push({
        file,
        id: `${relative || file.name}-${file.size}-${i}`,
        name: activeTab === 'folder' && relative ? relative : file.name,
      });
    }
    setSelected((prev) => {
      const alreadyPicked = new Set(prev.map((p) => p.id));
      return [...prev, ...items.filter((i) => !alreadyPicked.has(i.id))];
    });
    setError(null);
  };

  const handleBrowseClick = () => {
    const input = activeTab === 'folder' ? folderInputRef : fileInputRef;
    input.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    collect(e.target.files);
    e.target.value = '';
  };

  const handleUpload = async () => {
    if (!deploymentId || selected.length === 0) return;
    setError(null);

    const batch = selected;
    const tag = `batch-${batchSeq.current}`;
    batchSeq.current += 1;
    setSelected([]);
    setBatches((prev) => [
      ...prev,
      {
        tag,
        bytes: batch.reduce((sum, item) => sum + item.file.size, 0),
        progress: idleProgress(),
      },
    ]);

    const results = await uploadFiles(batch, deploymentId, (next) =>
      setBatches((prev) =>
        prev.map((slot) =>
          slot.tag === tag ? { ...slot, progress: next } : slot,
        ),
      ),
    );
    const failures = results.filter((r) => r.error);

    onUploaded?.(results);

    if (failures.length > 0) {
      setError(
        `${failures.length} of ${results.length} file(s) failed. ${failures[0].error}`,
      );
    }
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: '780px',
          height: '460px',
          background: '#0C0C0C',
          border: '1px solid #1A1A1A',
          borderRadius: '16px',
          boxShadow: '0 24px 72px rgba(0,0,0,0.8)',
          overflow: 'hidden',
          p: 0,
          m: 0,
        },
      }}
    >
      <IconButton
        onClick={onClose}
        sx={{
          position: 'absolute',
          top: 16,
          right: 16,
          color: '#666',
          zIndex: 10,
          '&:hover': { color: '#fff', background: 'rgba(255,255,255,0.05)' },
        }}
      >
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>

      <DialogContent sx={{ display: 'flex', p: 0, height: '100%' }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 4.5, pr: 3.5, boxSizing: 'border-box' }}>
          <Box sx={{ mb: 4.5 }}>
            <Typography
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '18px',
                fontWeight: 700,
                color: '#fff',
                letterSpacing: '1px',
                textTransform: 'uppercase',
                mb: 0.5,
              }}
            >
              Upload Acoustic Data
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '11px',
                color: '#666',
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              Import wildlife audio recordings for biodiversity and acoustic analysis.
            </Typography>
          </Box>

          {!complete && (
            <Box sx={{ display: 'flex', gap: 1.5, mb: 4 }}>
              <TabButton
                active={activeTab === 'files'}
                icon={<CloudUploadIcon sx={{ fontSize: 13 }} />}
                label="Upload Files"
                onClick={() => setActiveTab('files')}
              />
              <TabButton
                active={activeTab === 'folder'}
                icon={<FolderIcon sx={{ fontSize: 13 }} />}
                label="Upload Folder"
                onClick={() => setActiveTab('folder')}
              />
              <TabButton
                active={activeTab === 'drive'}
                icon={<GoogleDriveLogo style={{ fontSize: 13 }} />}
                label="Google Drive"
                onClick={() => setActiveTab('drive')}
              />
            </Box>
          )}

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            multiple
            accept={ACCEPT_ATTR}
            style={{ display: 'none' }}
          />
          <input
            type="file"
            ref={folderInputRef}
            onChange={handleFileChange}
            multiple
            {...({
              webkitdirectory: 'true',
              directory: '',
            } as React.InputHTMLAttributes<HTMLInputElement>)}
            style={{ display: 'none' }}
          />

          {complete ? (
            <Alert
              severity="info"
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                bgcolor: 'rgba(59, 130, 246, 0.1)',
                color: '#93C5FD',
                border: '1px solid rgba(59, 130, 246, 0.2)',
                borderRadius: '16px',
                '& .MuiAlert-icon': { color: '#3B82F6' },
              }}
            >
              This deployment has been marked as complete. No further uploads are possible. If you
              need to upload additional data, please contact administrative support for assistance.
            </Alert>
          ) : activeTab === 'drive' ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '13px',
                  fontWeight: 700,
                  color: '#fff',
                  textTransform: 'uppercase',
                  letterSpacing: '0.8px',
                  mb: 0.5,
                }}
              >
                Import from Google Drive
              </Typography>
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '11px',
                  color: '#555',
                  mb: 2.5,
                }}
              >
                Google drive folder or file link
              </Typography>

              <TextField
                fullWidth
                variant="outlined"
                placeholder="Paste google drive folder or file URL here"
                value={driveUrl}
                onChange={(e) => setDriveUrl(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LinkIcon sx={{ color: '#444', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    height: '42px',
                    backgroundColor: 'rgba(255,255,255,0.01)',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    borderRadius: '8px',
                    fontSize: '12px',
                    color: '#fff',
                    fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                    '& fieldset': { border: 'none' },
                    '&:hover': {
                      border: '1px solid rgba(255, 255, 255, 0.12)',
                    },
                    '&.Mui-focused': {
                      border: '1px solid #10B981',
                      boxShadow: '0 0 10px rgba(16, 185, 129, 0.15)',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: '#444',
                    opacity: 1,
                  },
                }}
              />

              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '10.5px',
                  color: '#555',
                  lineHeight: 1.45,
                  maxWidth: '380px',
                }}
              >
                Make sure the link shared is public. with enabled <br />
                anyone can view share option.
              </Typography>
            </Box>
          ) : (
            <Box
              onClick={handleBrowseClick}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                collect(e.dataTransfer.files);
              }}
              sx={{
                flex: 1,
                border: '1.5px dashed rgba(255,255,255,0.06)',
                borderRadius: '12px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                background: 'rgba(255,255,255,0.01)',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'rgba(16, 185, 129, 0.3)',
                  background: 'rgba(16, 185, 129, 0.01)',
                },
              }}
            >
              {activeTab === 'folder' ? (
                <FolderIcon sx={{ fontSize: 32, color: '#10B981', mb: 2, opacity: 0.8 }} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 32, color: '#10B981', mb: 2, opacity: 0.8 }} />
              )}
              <Typography
                sx={{
                  fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                  fontSize: '14px',
                  color: '#aaa',
                  mb: 1.5,
                  fontWeight: 500,
                  textAlign: 'center',
                }}
              >
                Drag & drop audio {activeTab === 'folder' ? 'folders' : 'files'} <br />
                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}>or </Typography>
                <Typography component="span" sx={{ color: '#10B981', fontSize: '14px', fontWeight: 700, textDecoration: 'underline' }}>browse</Typography>
                <Typography component="span" sx={{ color: '#666', fontSize: '14px' }}> from your device</Typography>
              </Typography>

              {activeTab === 'files' && (
                <Box sx={{ display: 'flex', gap: 1, mb: 1.5, mt: 0.5 }}>
                  {ACCEPTED_EXTENSIONS.map((ext) => (
                    <Box
                      key={ext}
                      sx={{
                        fontSize: '9px',
                        fontWeight: 700,
                        color: '#10B981',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        background: 'rgba(16, 185, 129, 0.05)',
                        borderRadius: '4px',
                        px: 0.8,
                        py: 0.2,
                      }}
                    >
                      {ext.toUpperCase()}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}

          {!complete && activeTab !== 'drive' && (
          <Box sx={{ mt: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {selected.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Typography sx={{ fontSize: '11px', color: '#999' }}>
                  {selected.length} file{selected.length > 1 ? 's' : ''} selected
                </Typography>
                <Button
                  size="small"
                  onClick={() => setSelected([])}
                  sx={{ fontSize: '10px', color: '#666' }}
                >
                  Clear
                </Button>
              </Box>
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!deploymentId || selected.length === 0}
                startIcon={<CloudUploadIcon sx={{ fontSize: 15 }} />}
                sx={{
                  fontSize: '10.5px',
                  fontWeight: 800,
                  background: '#10B981',
                  color: '#000',
                  px: 2.5,
                  height: '34px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderRadius: '6px',
                  boxShadow: 'none',
                  '&:hover': { background: '#0D9268', boxShadow: 'none' },
                  '&.Mui-disabled': { background: 'rgba(255,255,255,0.05)', color: '#555' },
                }}
              >
                Upload
              </Button>
              {!deploymentId && (
                <Typography sx={{ fontSize: '10px', color: '#666' }}>
                  Select a deployment to upload into.
                </Typography>
              )}
            </Box>

            {error && (
              <Typography sx={{ fontSize: '10.5px', color: '#F59E0B' }}>{error}</Typography>
            )}
            </Box>
          )}
        </Box>

        {!complete && (
          <Box
            sx={{
              width: '260px',
              background: 'linear-gradient(180deg, #111 0%, #0D0D0D 100%)',
              borderLeft: '1px solid #1A1A1A',
              p: 4.5,
              pt: 5,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                mb: 3,
              }}
            >
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: 'absolute', inset: 0 }}
              >
                <path
                  d="M12 2L20 7V17L12 22L4 17V7L12 2Z"
                  stroke="#10B981"
                  strokeWidth="1.5"
                  fill="rgba(16, 185, 129, 0.05)"
                />
              </svg>
              <Typography sx={{ color: '#10B981', fontSize: '18px', fontWeight: 900 }}>!</Typography>
            </Box>

            <Typography
              sx={{
                fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                fontSize: '11px',
                fontWeight: 700,
                color: '#10B981',
                letterSpacing: '1.5px',
                textTransform: 'uppercase',
                mb: 3.5,
                textAlign: 'center',
              }}
            >
              {panel.heading[0]} <br /> {panel.heading[1]}
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', mb: 4.5 }}>
              {panel.items.map((item) => {
                const Icon = item.icon;
                return (
                  <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Icon sx={{ fontSize: 13, color: '#10B981', opacity: 0.8 }} />
                    <Typography
                      sx={{
                        fontFamily: "'Century Gothic', Poppins, Arial, sans-serif",
                        fontSize: '11px',
                        color: '#999',
                        fontWeight: 500,
                      }}
                    >
                      {item.label}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <WaveGraphic />
          </Box>
        )}
      </DialogContent>
    </Dialog>
      {showingBatch &&
        createPortal(
          <BatchProgressCard
            progress={progress}
            settled={batchSettled}
            failed={failed}
            onDismiss={reset}
          />,
          document.body,
        )}
    </>
  );
};

export default UploadAudioModal;
